import { IRegEdSubmissionMetadata } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { CampaignType } from '../../../../models/AdminModels';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { accountHasComplianceEnabled } from '../../../../models/UiUtils';
import {
	invalidateBlogPostDrafts,
	useBlogSaveMutation,
	useBlogUpdateMutation,
	useDeleteBlogPostMutation,
} from '../../../../queries';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../components/ConfirmationDialog';
import { ComplianceApprovalPrompt } from '../../../components/campaigns/ComplianceApprovalPrompt';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../styles/styles';
import { CampaignSessionManager } from '../../shared/AIScheduleSuccess/campaignSessionManager';
import {
	campaignSessionStore,
	clearGlobalAISourceData,
	shouldOpenAIDrawer,
} from '../../shared/AIScheduleSuccess/campaignSessionStore';
import { UniversalAssistantChatWithinComposer } from '../../templates/AssistantChat';
import { BlogPostEditor } from './BlogPostEditor';
import { BlogPostPreviewAction } from './BlogPostPreviewAction';
import {
	BlogPostEditorContext,
	ImageStage,
	REDUCER_ACTION_KEYS,
	useBlogPostEditingRequest,
	validateContent,
} from './hooks';
import { IBlogPostComponentProps } from './models';
import { BlogPostEditorLayout } from './presentation';
import { styleSheet } from './styles';
import { Button } from '../../../components-v2/Button';

type CreateBlogPostProps = IImpersonationContextComponentProps & IBlogPostComponentProps;

function CreateBlogPostBase({ impersonationContext, onScheduleClicked }: CreateBlogPostProps) {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();

	const errorMessages = useErrorMessages();
	const requiresCompliance = accountHasComplianceEnabled(userSession, impersonationContext);

	const [showingAIAssistant, setShowingAIAssistant] = React.useState(() => {
		const shouldOpen = shouldOpenAIDrawer();
		if (shouldOpen) {
			clearGlobalAISourceData();
		}
		return shouldOpen;
	});

	const handleModalClose = React.useCallback(() => {
		CampaignSessionManager.clearSession();
		fullscreenModal.dismissModal();
	}, [fullscreenModal]);

	const sourceContent = campaignSessionStore.aiSourceContent;
	const sourceType = campaignSessionStore.aiSourceType;

	const {
		postEditorContext,
		blogPost,
		setBlogPost,
		setSendFromUser,
		setRegEdMetadata,
		regEdMetadata: savedRegEdMetadata,
		request,
	} = useBlogPostEditingRequest({
		impersonationContext,
	});
	const { state, dispatch } = postEditorContext;

	const hasImageSelected = Boolean(state.selectedImage);

	React.useEffect(() => {
		if (!hasImageSelected && state.imageState === ImageStage.Loading) {
			dispatch({
				type: REDUCER_ACTION_KEYS.GENERATING_CONTENT,
				payload: { imageSuggestingLoading: false, imageState: ImageStage.Loaded },
			});
		}
	}, [hasImageSelected, state.imageState, dispatch]);

	const saveBlogMutation = useBlogSaveMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSuccess: blog => {
			setBlogPost(blog);
			toast.success('Blog post saved.');
			invalidateBlogPostDrafts({ userId: userSession.user.id });
		},
	});

	const updateBlogMutation = useBlogUpdateMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSuccess: blog => {
			setBlogPost(blog);
			toast.success('Blog post saved.');
			invalidateBlogPostDrafts({ userId: userSession.user.id });
		},
	});

	const deleteBlogPostMutation = useDeleteBlogPostMutation({
		impersonationContext,
		onSuccess: () => {
			CampaignSessionManager.clearSession();
			fullscreenModal.dismissModal();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const handleScheduleClick = (
		sendWithCompliance = false,
		sendWithComplianceEmail?: string,
		regEdMetadata?: IRegEdSubmissionMetadata
	) => {
		const errors = validateContent(state);
		if (errors.length !== 0) {
			errorMessages.push({
				messages: errors,
			});
			return;
		}

		const postToSave: Api.IBlogPost = {
			...blogPost,
			content: state.content.getRawRichTextContent(),
			title: state.title,
			mainImage: state.selectedImage,
			id: state.blogId,
			sendFromUserId: state.sendFromUser?.id,
			scheduledSendDate: blogPost?.scheduledSendDate || request?.schedule?.startDate,
			sendWithCompliance,
			sendWithComplianceEmail,
		};

		setRegEdMetadata(regEdMetadata);
		setBlogPost(postToSave);
		if (state.sendFromUser) {
			setSendFromUser(state.sendFromUser);
		}

		if (!onScheduleClicked?.(postToSave)) {
			fullscreenModal.history.push({
				pathname: '/blog/post/schedule',
			});
		}
	};

	const handleDeleteClick = () => {
		dispatch({ type: REDUCER_ACTION_KEYS.DELETE_BLOG_POST, payload: { value: true } });
	};

	const onDeleteConfirmationRequestClose = React.useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			if (result?.isDestructive && !cancel) {
				deleteBlogPostMutation.mutate({ id: state.blogId });
			}
			dispatch({ type: REDUCER_ACTION_KEYS.DELETE_BLOG_POST, payload: { value: false } });
		},
		[deleteBlogPostMutation, dispatch, state.blogId]
	);
	const onUseAIBlog = (title: string, content: Api.IRawRichTextContentState) => {
		dispatch({
			type: REDUCER_ACTION_KEYS.USE_AI_BLOG,
			payload: {
				title,
				content,
			},
		});
	};

	const handleSaveClick = () => {
		if (blogPost?.id) {
			updateBlogMutation.mutate({
				post: {
					...blogPost,
					content: state.content.getRawRichTextContent(),
					title: state.title,
					mainImage: state.selectedImage,
					sendFromUserId: state.sendFromUser?.id,
				},
			});
			return;
		}
		saveBlogMutation.mutate({
			post: {
				content: state.content.getRawRichTextContent(),
				title: state.title,
				mainImage: state.selectedImage,
				id: undefined,
				sendFromUserId: state.sendFromUser?.id,
			},
		});
	};

	return (
		<BlogPostEditorContext.Provider value={postEditorContext}>
			<BlogPostEditorLayout
				status={state.status}
				header='Generate Blog'
				onClose={handleModalClose}
				subHeader={
					<>
						<h1>New Blog</h1>
						<div className={css(styleSheet.menu)}>
							{impersonationContext?.isValid ? null : (
								<Button
									variant='secondary'
									size='sm'
									onClick={handleSaveClick}
									disabled={!(state.content && state.title)}
								>
									Save Blog Post
								</Button>
							)}
							<BlogPostPreviewAction impersonationContext={impersonationContext} />
							{blogPost?.id ? (
								<Button variant='destructive-secondary' size='sm' onClick={handleDeleteClick}>
									Delete
								</Button>
							) : null}
						</div>
					</>
				}
				sidebar={
					<>
						{!showingAIAssistant && requiresCompliance ? (
							<ComplianceApprovalPrompt
								campaignType={CampaignType.Blog}
								styles={[styleSheet.regEdComplianceScroll]}
								regEdInitialFormat={savedRegEdMetadata?.format}
								regEdInitialAnswers={savedRegEdMetadata?.answers}
								onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
									e.preventDefault();
									handleScheduleClick(true, sendWithComplianceEmail);
								}}
								onScheduleRegEdCompliance={(e, metadata) => {
									e.preventDefault();
									handleScheduleClick(true, undefined, metadata);
								}}
								onScheduleWithoutCompliance={() => handleScheduleClick()}
							/>
						) : null}
						{!showingAIAssistant && !requiresCompliance ? (
							<div className={css(styleSheet.sidebarCenter)}>
								<h2 className={css(styleSheet.question)}>What would you like to do with this blog?</h2>
								<button type='button' className={css(baseStyleSheet.ctaButton)} onClick={() => handleScheduleClick()}>
									Schedule
								</button>
							</div>
						) : null}
						{/* We handle conitional rendering within the component, so we can manage the chat session. Don't wrap this in a conditional. */}
						<UniversalAssistantChatWithinComposer
							isOpen={showingAIAssistant}
							impersonationContext={impersonationContext?.toJs()}
							onClose={() => setShowingAIAssistant(false)}
							onUseBlogContent={onUseAIBlog}
							blogContext={{
								title: state.title,
								content: state.content.getRawRichTextContent(),
							}}
							emailContext={sourceType === 'email' && sourceContent ? (sourceContent as Api.EmailAIContext) : undefined}
							socialContext={
								sourceType === 'social' && sourceContent ? (sourceContent as Api.SocialMediaAIContext) : undefined
							}
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={{
								isOpen: state.showConfirmationModal,
								onRequestClose: onDeleteConfirmationRequestClose,
							}}
							options={DefaultDeleteConfirmationOptions}
							title='Are you sure you want to delete this blog post?'
						/>
					</>
				}
			>
				<BlogPostEditor onAIAssitantLinkClick={() => setShowingAIAssistant(true)} />
			</BlogPostEditorLayout>
		</BlogPostEditorContext.Provider>
	);
}

export const CreateBlogPost = inject(ImpersonationContextKey)(observer(CreateBlogPostBase));
